import { useState, useEffect } from 'react';
import { 
  Flex, 
  View, 
  Text, 
  Heading, 
  TextField, 
  Button,
  Alert,
  Card,
  Divider,
  Badge,
  Grid,
  Loader
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { fetchUserAttributes, deleteUser, updateUserAttributes } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

const UserProfile = ({ setUser }) => {
  const [userAttributes, setUserAttributes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const navigate = useNavigate();
  const [editedAttributes, setEditedAttributes] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes);
        setEditedAttributes(attributes);
      } catch (error) {
        console.error('Error fetching user attributes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, []);

  const handleDeleteAccount = async () => {
    try {
      await deleteUser();
      setUser(null);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError('Failed to delete account. Please try again.');
      setShowDeleteConfirmation(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedAttributes(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setUpdateError(null);
      setUpdateSuccess(false);

      const updatableFields = {
        given_name: editedAttributes.given_name,
        family_name: editedAttributes.family_name
      };

      await updateUserAttributes({
        userAttributes: updatableFields
      });

      setUserAttributes(editedAttributes);
      setIsEditing(false);
      setUpdateSuccess(true);

      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error updating user attributes:', error);
      setUpdateError('Failed to update profile. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedAttributes(userAttributes);
    setIsEditing(false);
    setUpdateError(null);
  };

  if (loading) {
    return (
      <View style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
        {/* Full page animated background */}
        <View
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            zIndex: -1
          }}
        />
        <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
          <View maxWidth="400px" margin="0 auto">
            <Card 
              borderRadius="12px" 
              padding="3rem"
              style={{ 
                background: 'rgba(30, 35, 45, 0.85)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                textAlign: 'center'
              }}
            >
              <Flex direction="column" alignItems="center" gap="1rem">
                <Loader size="large" />
                <Text fontSize="1.1rem" color="rgba(255,255,255,0.8)">Loading your profile...</Text>
              </Flex>
            </Card>
          </View>
        </View>
      </View>
    );
  }

  if (!userAttributes) {
    return (
      <View style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
        {/* Full page animated background */}
        <View
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            zIndex: -1
          }}
        />
        <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
          <View maxWidth="400px" margin="0 auto">
            <Card 
              borderRadius="12px" 
              padding="3rem"
              style={{ 
                background: 'rgba(30, 35, 45, 0.85)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                textAlign: 'center'
              }}
            >
              <Text fontSize="1.1rem" color="rgba(255,255,255,0.8)">No user data found</Text>
            </Card>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
      {/* Full page animated background */}
      <View
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: -1
        }}
      >
        {/* Animated bubbles covering entire background */}
        <View
          style={{
            position: 'absolute',
            top: '-100px',
            left: '-100px',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            animation: 'float 6s ease-in-out infinite'
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '-150px',
            right: '-150px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            animation: 'float 8s ease-in-out infinite reverse'
          }}
        />
        <View
          style={{
            position: 'absolute',
            top: '40%',
            left: '10%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
            animation: 'float 10s ease-in-out infinite'
          }}
        />
        <View
          style={{
            position: 'absolute',
            bottom: '30%',
            right: '20%',
            width: '350px',
            height: '350px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
            animation: 'float 12s ease-in-out infinite reverse'
          }}
        />
        <View
          style={{
            position: 'absolute',
            top: '60%',
            right: '40%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.09)',
            animation: 'float 15s ease-in-out infinite'
          }}
        />
      </View>

      <View padding="2rem" style={{ position: 'relative', zIndex: 1 }}>
        <Flex direction="column" gap="2rem" maxWidth="1400px" margin="0 auto">
          
          {/* Use Grid for responsive layout */}
          <Grid
            templateColumns={{ base: "1fr", large: "1.5fr 1fr" }}
            gap="2rem"
          >
            {/* Left Column - Profile Header and Details */}
            <Flex direction="column" gap="2rem">
              {/* Profile Header Card with gradient */}
              <Card 
                borderRadius="12px" 
                padding="2.5rem"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <View
                  style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)'
                  }}
                />
                <Flex direction="column" gap="1.5rem" position="relative">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center" gap="1.5rem">
                      <View
                        width="90px"
                        height="90px"
                        borderRadius="50%"
                        backgroundColor="rgba(255,255,255,0.2)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          border: '3px solid rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(10px)',
                          position: 'relative'
                        }}
                      >
                        <Text 
                          fontSize="2.5rem" 
                          color="white" 
                          fontWeight="bold"
                          style={{
                            lineHeight: '1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, calc(-50% - 3px))' // Adjust the 3px value to move up/down
                          }}
                        >
                          {userAttributes.given_name?.charAt(0) || 'U'}
                        </Text>
                      </View>
                      <Flex direction="column">
                        <Heading level={2} style={{ color: 'white' }}>
                          {userAttributes.given_name} {userAttributes.family_name}
                        </Heading>
                        <Text fontSize="1rem" style={{ color: 'rgba(255,255,255,0.8)' }}>
                          Welcome to your profile
                        </Text>
                      </Flex>
                    </Flex>
                    <Badge 
                      variation="success" 
                      size="large"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      Active
                    </Badge>
                  </Flex>
                </Flex>
              </Card>

              {/* Profile Details Card with dark theme */}
              <Card 
                borderRadius="12px" 
                padding="2rem"
                style={{ 
                  background: 'rgba(30, 35, 45, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                  minHeight: '450px' // Added to match the height with delete account section
                }}
              >
                <Flex direction="column" gap="1.5rem">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center" gap="0.75rem">
                      <Heading level={4} style={{ color: 'white' }}>Profile Information</Heading>
                    </Flex>
                    {!isEditing && (
                      <Button
                        variation="link"
                        onClick={() => setIsEditing(true)}
                        style={{ 
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          textDecoration: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          transition: 'all 0.3s',
                          boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 3px 10px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        Edit Profile
                      </Button>
                    )}
                  </Flex>

                  <Divider style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                  {/* Form Fields with dark theme */}
                  <Flex direction="column" gap="1.5rem">
                    {/* Email Field */}
                    <Flex direction="column" gap="0.5rem">
                      <Text fontWeight="bold" fontSize="0.9rem" color="rgba(255,255,255,0.9)">Email Address</Text>
                      <TextField
                        value={userAttributes.email || ''}
                        isReadOnly
                        placeholder="Enter your Email"
                        style={{ 
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          cursor: 'not-allowed',
                          borderRadius: '8px',
                          padding: '0.75rem',
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}
                      />
                      <Text fontSize="0.8rem" color="rgba(255,255,255,0.6)">
                        Email cannot be changed
                      </Text>
                    </Flex>

                    {/* First Name Field */}
                    <Flex direction="column" gap="0.5rem">
                      <Text fontWeight="bold" fontSize="0.9rem" color="rgba(255,255,255,0.9)">First Name</Text>
                      <TextField
                        value={editedAttributes.given_name || ''}
                        onChange={(e) => handleInputChange('given_name', e.target.value)}
                        placeholder="Enter your first name"
                        isReadOnly={!isEditing}
                        style={{ 
                          backgroundColor: isEditing ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.1)',
                          color: isEditing ? '#333' : 'white',
                          cursor: isEditing ? 'text' : 'not-allowed',
                          border: isEditing ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          padding: '0.75rem',
                          transition: 'all 0.2s'
                        }}
                      />
                    </Flex>

                    {/* Last Name Field */}
                    <Flex direction="column" gap="0.5rem">
                      <Text fontWeight="bold" fontSize="0.9rem" color="rgba(255,255,255,0.9)">Last Name</Text>
                      <TextField
                        value={editedAttributes.family_name || ''}
                        onChange={(e) => handleInputChange('family_name', e.target.value)}
                        placeholder="Enter your last name"
                        isReadOnly={!isEditing}
                        style={{ 
                          backgroundColor: isEditing ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.1)',
                          color: isEditing ? '#333' : 'white',
                          cursor: isEditing ? 'text' : 'not-allowed',
                          border: isEditing ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          padding: '0.75rem',
                          transition: 'all 0.2s'
                        }}
                      />
                    </Flex>

                    {/* Information Notice Card */}
                    <Card 
                      backgroundColor="rgba(102, 126, 234, 0.15)" 
                      padding="0.75rem 1rem" 
                      borderRadius="8px"
                      style={{ 
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                        marginTop: '0.5rem'
                      }}
                    >
                      <Flex direction="column" gap="0.25rem" alignItems="center">
                        <Text 
                          fontSize="0.95rem" 
                          fontWeight="bold" 
                          color="rgba(255,255,255,0.95)"
                          textAlign="center"
                        >
                          Account Information
                        </Text>
                        <Text 
                          fontSize="0.8rem" 
                          color="rgba(255,255,255,0.85)" 
                          lineHeight="1.4"
                          textAlign="center"
                        >
                          Personal details can be updated at any time. Email addresses are permanent for security 
                          and data integrity purposes.
                        </Text>
                      </Flex>
                    </Card>
                  </Flex>

                  {/* Success/Error Alerts */}
                  {updateSuccess && (
                    <Alert 
                      variation="success" 
                      isDismissible
                      style={{
                        borderRadius: '8px',
                        animation: 'slideIn 0.3s ease-out'
                      }}
                    >
                      Profile updated successfully!
                    </Alert>
                  )}

                  {updateError && (
                    <Alert 
                      variation="error" 
                      isDismissible
                      style={{
                        borderRadius: '8px',
                        animation: 'slideIn 0.3s ease-out'
                      }}
                    >
                      {updateError}
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  {isEditing ? (
                    <Flex direction="row" gap="1rem" marginTop="1rem" justifyContent="center">
                      <Button 
                        variation="primary" 
                        onClick={handleSave}
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.75rem 2rem',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: 'white',
                          transition: 'all 0.3s',
                          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        Save Changes
                      </Button>
                      <Button 
                        variation="secondary" 
                        onClick={handleCancel}
                        style={{
                          borderRadius: '8px',
                          padding: '0.75rem 2rem',
                          fontSize: '1rem',
                          fontWeight: '600',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          border: '2px solid rgba(255,255,255,0.3)',
                          color: 'white',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        Cancel
                      </Button>
                    </Flex>
                  ) : null}
                </Flex>
              </Card>
            </Flex>

            {/* Right Column - Account Info and Deletion */}
            <Flex direction="column" gap="2rem">
              {/* Account Information Card */}
              <Card 
                borderRadius="12px" 
                padding="2rem"
                style={{ 
                  background: 'rgba(30, 35, 45, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              >
                <Flex direction="column" gap="1rem">
                  <Heading level={5} style={{ color: 'white' }}>Account Information</Heading>
                  <Flex direction="column" gap="0.75rem">
                    <Card 
                      backgroundColor="rgba(255,255,255,0.05)" 
                      padding="0.75rem 1rem" 
                      borderRadius="8px"
                      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text color="rgba(255,255,255,0.7)">Email Status</Text>
                        <Badge 
                          variation={userAttributes.email_verified ? "success" : "warning"}
                          style={{ borderRadius: '20px' }}
                        >
                          {userAttributes.email_verified ? 'Verified' : 'Not Verified'}
                        </Badge>
                      </Flex>
                    </Card>
                    
                    <Card 
                      backgroundColor="rgba(255,255,255,0.05)" 
                      padding="0.75rem 1rem" 
                      borderRadius="8px"
                      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text color="rgba(255,255,255,0.7)">Account Type</Text>
                        <Text fontWeight="bold" color="white">Standard User</Text>
                      </Flex>
                    </Card>
                    
                    <Card 
                      backgroundColor="rgba(255,255,255,0.05)" 
                      padding="0.75rem 1rem" 
                      borderRadius="8px"
                      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text color="rgba(255,255,255,0.7)">User ID</Text>
                        <Text 
                          fontFamily="monospace" 
                          fontSize="0.9rem"
                          backgroundColor="rgba(255,255,255,0.1)"
                          padding="0.25rem 0.5rem"
                          borderRadius="4px"
                          color="white"
                        >
                          {userAttributes.sub?.substring(0, 8)}...
                        </Text>
                      </Flex>
                    </Card>
                  </Flex>
                </Flex>
              </Card>

              {/* Account Deletion Card */}
              <Card 
                borderRadius="12px" 
                padding="2rem"
                style={{
                  background: 'rgba(231, 76, 60, 0.15)',
                  backdropFilter: 'blur(20px)',
                  border: '2px solid rgba(231, 76, 60, 0.5)',
                  boxShadow: '0 10px 30px rgba(231, 76, 60, 0.2)'
                }}
              >
                <Flex direction="column" gap="1.5rem" alignItems="center">
                  <View textAlign="center">
                    <Heading level={4} style={{ color: 'white' }}>Delete Account</Heading>
                    <Text color="rgba(255,255,255,0.9)" fontSize="0.95rem" marginTop="0.5rem">
                      Permanently remove your account and all associated data
                    </Text>
                  </View>
                  
                  <Card 
                    backgroundColor="rgba(0,0,0,0.2)" 
                    padding="1.25rem" 
                    borderRadius="8px" 
                    width="100%"
                    style={{ border: '1px solid rgba(231, 76, 60, 0.3)' }}
                  >
                    <Flex direction="column" gap="0.75rem">
                      <Text fontSize="0.9rem" color="white">
                        <strong>This action will:</strong>
                      </Text>
                      <ul style={{ marginLeft: '1.5rem', color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem', lineHeight: '1.6' }}>
                        <li>Permanently delete your profile and CV data</li>
                        <li>Remove all your saved internships</li>
                        <li>Remove your CV analysis history</li>
                        <li>Delete all personal information from our systems</li>
                      </ul>
                      <View 
                        backgroundColor="rgba(231, 76, 60, 0.3)" 
                        padding="0.5rem 1rem" 
                        borderRadius="6px"
                        textAlign="center"
                        marginTop="0.5rem"
                        style={{ border: '1px solid rgba(231, 76, 60, 0.5)' }}
                      >
                        <Text fontSize="0.85rem" color="white" fontWeight="bold">
                          This action cannot be undone
                        </Text>
                      </View>
                    </Flex>
                  </Card>
                  
                  {!showDeleteConfirmation ? (
                    <Button 
                      variation="destructive" 
                      onClick={() => setShowDeleteConfirmation(true)}
                      size="large"
                      style={{
                        backgroundColor: '#e74c3c',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.75rem 2rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: 'white',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#c0392b';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(231, 76, 60, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#e74c3c';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      Delete Account
                    </Button>
                  ) : (
                    <Card 
                      borderRadius="8px" 
                      padding="1.5rem" 
                      backgroundColor="rgba(30, 35, 45, 0.95)" 
                      width="100%"
                      style={{
                        border: '2px solid #e74c3c',
                        animation: 'slideIn 0.3s ease-out'
                      }}
                    >
                      <Flex direction="column" gap="1rem" alignItems="center">
                        <Alert 
                          variation="warning"
                          isDismissible={false}
                          style={{ borderRadius: '8px' }}
                        >
                          <Flex direction="column" gap="0.5rem">
                            <Text fontWeight="bold">Final Confirmation</Text>
                            <Text fontSize="0.9rem">
                              Are you absolutely sure? This cannot be undone.
                            </Text>
                          </Flex>
                        </Alert>
                        
                        <Flex direction="column" gap="1rem" width="100%">
                          <Button 
                            variation="destructive"
                            onClick={handleDeleteAccount}
                            width="100%"
                            style={{
                              backgroundColor: '#e74c3c',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '0.75rem',
                              fontSize: '1rem',
                              fontWeight: '600',
                              color: 'white'
                            }}
                          >
                            Yes, Permanently Delete My Account
                          </Button>
                          <Button 
                            variation="secondary"
                            onClick={() => setShowDeleteConfirmation(false)}
                            width="100%"
                            style={{
                              borderRadius: '8px',
                              padding: '0.75rem',
                              fontSize: '1rem',
                              fontWeight: '600',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              border: '2px solid rgba(255,255,255,0.3)',
                              color: 'white'
                            }}
                          >
                            Cancel
                          </Button>
                        </Flex>

                        {deleteError && (
                          <Alert variation="error" style={{ borderRadius: '8px' }}>
                            {deleteError}
                          </Alert>
                        )}
                      </Flex>
                    </Card>
                  )}
                </Flex>
              </Card>
            </Flex>
          </Grid>
        </Flex>
      </View>

      <style jsx>{`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-30px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </View>
  );
};

export default UserProfile;